'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { usePlatformAuth } from '@/context/PlatformAuthContext'
import api from '@/lib/platform-api'
import { getUserGreetingName } from '@/lib/userDisplay'
import {
  Search, GitCompare, ChefHat, ArrowRight, Zap, Database, Shield,
  BarChart3, TrendingUp, Plug, Calendar, ShoppingBag, Store,
} from 'lucide-react'

const featureCards = [
  {
    id: 'search', title: 'Smart Search', route: '/search', badge: 'AI Powered',
    description: 'Upload CSV catalogs and run AI semantic search with intelligent relevance ranking.',
    icon: Search, color: '#3b82f6',
    gradient: 'linear-gradient(135deg,#3b82f6 0%,#1d4ed8 100%)',
    glow: 'rgba(59,130,246,0.25)',
  },
  {
    id: 'compare', title: 'Compare Datasets', route: '/compare', badge: 'Analysis',
    description: 'Side-by-side analysis, differences, and patterns across multiple sources.',
    icon: GitCompare, color: '#10b981',
    gradient: 'linear-gradient(135deg,#10b981 0%,#059669 100%)',
    glow: 'rgba(16,185,129,0.25)',
  },
  {
    id: 'recipes', title: 'Recipe Search', route: '/recipes', badge: 'Discovery',
    description: 'Find recipes by ingredient with cooking instructions and prep times.',
    icon: ChefHat, color: '#f59e0b',
    gradient: 'linear-gradient(135deg,#f59e0b 0%,#d97706 100%)',
    glow: 'rgba(245,158,11,0.25)',
  },
  {
    id: 'predict', title: 'Requirements Prediction', route: '/predict', badge: 'XGBoost',
    description: 'Forecast next week\'s product needs from your order history.',
    icon: TrendingUp, color: '#8b5cf6',
    gradient: 'linear-gradient(135deg,#8b5cf6 0%,#6d28d9 100%)',
    glow: 'rgba(139,92,246,0.25)',
  },
  {
    id: 'stores', title: 'Ecommerce Stores', route: '/dashboard', badge: 'New',
    description: 'Upload a CSV catalog and generate a fully live storefront instantly.',
    icon: Store, color: '#06b6d4',
    gradient: 'linear-gradient(135deg,#06b6d4 0%,#0284c7 100%)',
    glow: 'rgba(6,182,212,0.25)',
  },
]

const highlights = [
  { icon: Zap, label: 'Lightning Fast', sub: 'Results in milliseconds' },
  { icon: Database, label: 'Any CSV', sub: 'Upload any dataset' },
  { icon: Shield, label: 'Private', sub: 'Your data stays secure' },
  { icon: BarChart3, label: 'Analytics', sub: 'Track search metrics' },
  { icon: ShoppingBag, label: 'Storefronts', sub: 'CSV → live shop' },
]

function AnimatedCount({ value, duration = 1000 }: { value: number; duration?: number }) {
  const [display, setDisplay] = useState(0)
  useEffect(() => {
    if (!value) return
    const steps = 30
    const increment = value / steps
    let current = 0
    const timer = setInterval(() => {
      current += increment
      if (current >= value) { setDisplay(value); clearInterval(timer) }
      else setDisplay(Math.floor(current))
    }, duration / steps)
    return () => clearInterval(timer)
  }, [value, duration])
  return <span>{display.toLocaleString()}</span>
}

interface Stats {
  success: boolean
  searches_this_month: number
  searches_all_time: number
  connections_this_month: number
  connections_all_time: number
  top_search_type: string | null
  month: string
}

export default function HomePage() {
  const router = useRouter()
  const { user, token, loading } = usePlatformAuth()
  const [hovered, setHovered] = useState<string | null>(null)
  const [stats, setStats] = useState<Stats | null>(null)
  const [statsLoading, setStatsLoading] = useState(true)

  const greetingFirst = getUserGreetingName(user)
  const hasActivity = stats && (stats.searches_all_time > 0 || stats.connections_all_time > 0)

  useEffect(() => {
    api.get('/user-stats/')
      .then(res => { if (res.data.success) setStats(res.data) })
      .catch(() => {})
      .finally(() => setStatsLoading(false))
  }, [])

  const statItems = stats ? [
    { icon: Search, label: 'Searches This Month', value: stats.searches_this_month, sub: `${stats.searches_all_time.toLocaleString()} all time`, color: '#3b82f6', glow: 'rgba(59,130,246,0.15)', gradient: 'linear-gradient(135deg,#3b82f6 0%,#1d4ed8 100%)' },
    { icon: Plug, label: 'New Connections', value: stats.connections_this_month, sub: `${stats.connections_all_time.toLocaleString()} all time`, color: '#10b981', glow: 'rgba(16,185,129,0.15)', gradient: 'linear-gradient(135deg,#10b981 0%,#059669 100%)' },
    { icon: TrendingUp, label: 'Top Search Type', value: null, text: stats.top_search_type ? stats.top_search_type.charAt(0).toUpperCase() + stats.top_search_type.slice(1) : '—', sub: 'most used this month', color: '#f59e0b', glow: 'rgba(245,158,11,0.15)', gradient: 'linear-gradient(135deg,#f59e0b 0%,#d97706 100%)' },
    { icon: Calendar, label: 'Activity Period', value: null, text: stats.month, sub: 'current billing period', color: '#8b5cf6', glow: 'rgba(139,92,246,0.15)', gradient: 'linear-gradient(135deg,#8b5cf6 0%,#6d28d9 100%)' },
  ] : []

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="relative overflow-hidden text-center px-6 pt-12 pb-8">
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-[500px] h-[300px] rounded-full opacity-10 bg-blue-500 blur-3xl pointer-events-none" />

        {/* Welcome badge */}
        <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 mb-6 text-sm text-slate-500 shadow-sm">
          <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />
          {token ? (
            loading && !user ? <>Welcome back…</> :
            greetingFirst ? <><span>Welcome back,</span> <strong className="text-slate-800">{greetingFirst}</strong></> :
            <>Welcome back</>
          ) : <>AI-powered search & analytics</>}
        </div>

        <h1 className="text-6xl md:text-7xl font-black tracking-tight leading-none mb-5 bg-gradient-to-r from-blue-500 via-emerald-500 to-amber-400 bg-clip-text text-transparent">
          QJUMP
        </h1>
        <p className="text-lg text-slate-500 max-w-md mx-auto mb-8 leading-relaxed">
          AI-powered search, data analysis, and ecommerce.<br />Upload your data, find insights instantly.
        </p>

        <div className="flex gap-3 justify-center flex-wrap">
          <button onClick={() => router.push('/search')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white font-bold shadow-lg shadow-blue-500/30 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-500/40 transition-all">
            Open Search <ArrowRight size={16} />
          </button>
          <button onClick={() => router.push('/dashboard')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white border border-slate-200 text-slate-700 font-semibold hover:-translate-y-0.5 hover:shadow-md transition-all">
            My Stores
          </button>
        </div>
      </div>

      {/* Stats */}
      {!statsLoading && hasActivity && (
        <div className="max-w-4xl mx-auto px-6 pb-10">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={14} className="text-slate-400" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
              Your Activity — {stats!.month}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {statItems.map(({ icon: Icon, label, value, text, sub, color, glow, gradient }) => (
              <div key={label}
                className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3 transition-all hover:shadow-lg"
                style={{ '--hover-glow': glow } as React.CSSProperties}
                onMouseEnter={e => (e.currentTarget.style.boxShadow = `0 8px 24px ${glow}`)}
                onMouseLeave={e => (e.currentTarget.style.boxShadow = '')}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: gradient }}>
                  <Icon size={16} />
                </div>
                <div>
                  {value !== null ? (
                    <div className="text-3xl font-extrabold text-slate-900"><AnimatedCount value={value!} /></div>
                  ) : (
                    <div className="text-2xl font-extrabold text-slate-900">{text}</div>
                  )}
                  <div className="text-xs font-semibold text-slate-700 mt-1">{label}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Highlight pills */}
      <div className="flex justify-center flex-wrap gap-2 px-6 pb-10">
        {highlights.map(({ icon: Icon, label, sub }) => (
          <div key={label} className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-sm shadow-sm">
            <Icon size={13} className="text-slate-400" />
            <span className="font-semibold text-slate-700">{label}</span>
            <span className="text-slate-400">— {sub}</span>
          </div>
        ))}
      </div>

      {/* Feature cards */}
      <div className="max-w-5xl mx-auto px-6 pb-20">
        <h2 className="text-center text-2xl font-bold text-slate-800 mb-2">What would you like to do?</h2>
        <p className="text-center text-slate-500 mb-8">Pick a tool to get started</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featureCards.map((card) => {
            const Icon = card.icon
            const isHov = hovered === card.id
            return (
              <button key={card.id} onClick={() => router.push(card.route)}
                onMouseEnter={() => setHovered(card.id)}
                onMouseLeave={() => setHovered(null)}
                className="text-left bg-white border rounded-2xl p-7 flex flex-col gap-4 cursor-pointer transition-all duration-300"
                style={{
                  borderColor: isHov ? card.color + '60' : '#e2e8f0',
                  transform: isHov ? 'translateY(-6px)' : 'translateY(0)',
                  boxShadow: isHov ? `0 20px 40px ${card.glow}, 0 8px 16px rgba(0,0,0,0.06)` : '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                <div className="flex justify-between items-start">
                  <div className="w-13 h-13 rounded-2xl flex items-center justify-center text-white transition-transform duration-300"
                    style={{
                      width: 52, height: 52, background: card.gradient,
                      boxShadow: `0 6px 18px ${card.glow}`,
                      transform: isHov ? 'scale(1.08) rotate(4deg)' : 'scale(1)',
                    }}>
                    <Icon size={24} />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full"
                    style={{ color: card.color, background: card.color + '18' }}>
                    {card.badge}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800 mb-1">{card.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{card.description}</p>
                </div>
                <div className="flex items-center gap-1.5 text-sm font-semibold transition-all duration-300"
                  style={{ color: card.color, opacity: isHov ? 1 : 0, transform: isHov ? 'translateX(0)' : 'translateX(-8px)' }}>
                  Open <ArrowRight size={13} />
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
